import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import styles from "./Commission.module.css";

const Commission = () => {
  const [currentUsername, setCurrentUsername] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [summary, setSummary] = useState({
    thisWeek: 0,
    lastWeek: 0,
    thisMonth: 0,
  });

  const [todayBreakdown, setTodayBreakdown] = useState({
    total: 0,
    level1: 0,
    level2: 0,
    level3: 0,
  });

  /* ================= USER ================= */
  useEffect(() => {
    const username = localStorage.getItem("username");
    setCurrentUsername(username);
  }, []);

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchCommissions = async () => {
      if (!currentUsername) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const ref = collection(db, "commissions");
        const q = query(
          ref,
          where("username", "==", currentUsername),
          limit(100)
        );

        const snapshot = await getDocs(q);

        const data = snapshot.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            ...d,
            changedAt:
              d.createdAt && typeof d.createdAt.toDate === "function"
                ? d.createdAt.toDate()
                : null,
          };
        });

        data.sort((a, b) =>
          b.changedAt && a.changedAt ? b.changedAt - a.changedAt : 0
        );

        setCommissions(data);
        calculateSummary(data);
      } catch (err) {
        console.error(err);
        setError("Failed to load commission data");
      } finally {
        setLoading(false);
      }
    };

    fetchCommissions();
  }, [currentUsername]);

  /* ================= HELPERS ================= */
  const isToday = (date) => {
    const now = new Date();
    return (
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear()
    );
  };

  /* ================= SUMMARY ================= */
  const calculateSummary = (data) => {
    const now = new Date();

    const startOfThisWeek = new Date(now);
    startOfThisWeek.setDate(now.getDate() - now.getDay());
    startOfThisWeek.setHours(0, 0, 0, 0);

    const startOfLastWeek = new Date(startOfThisWeek);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);

    const endOfLastWeek = new Date(startOfThisWeek);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    let thisWeek = 0;
    let lastWeek = 0;
    let thisMonth = 0;

    let todayTotal = 0;
    let l1 = 0,
      l2 = 0,
      l3 = 0;

    data.forEach((c) => {
      if (!c.changedAt || c.status !== "success") return;

      const amount = Number(c.amount) || 0;
      const date = c.changedAt;

      if (date >= startOfThisWeek) thisWeek += amount;
      if (date >= startOfLastWeek && date < endOfLastWeek)
        lastWeek += amount;
      if (date >= startOfMonth) thisMonth += amount;

      // 🔥 LEO + LEVELS (SYSTEM DATA ONLY)
      if (isToday(date)) {
        todayTotal += amount;
        if (c.level === 1) l1 += amount;
        if (c.level === 2) l2 += amount;
        if (c.level === 3) l3 += amount;
      }
    });

    setSummary({
      thisWeek,
      lastWeek,
      thisMonth,
    });

    setTodayBreakdown({
      total: todayTotal,
      level1: l1,
      level2: l2,
      level3: l3,
    });
  };

  /* ================= UI STATES ================= */
  if (!currentUsername) {
    return <div>Please log in to view your commission.</div>;
  }

  if (loading) return <div>Loading...</div>;

  if (error) return <div style={{ color: "red" }}>{error}</div>;

  if (commissions.length === 0) {
    return <div>Bado auja pata commission .</div>;
  }

  /* ================= UI ================= */
  return (
    <div className={styles.container}>
      <h2>Commission</h2>

      {/* ===== SUMMARY ===== */}
      <div className={styles.summary}>
        <div className={styles.card}>
          <h4>This Week</h4>
          <p>{summary.thisWeek.toLocaleString()} </p>
        </div>
        <div className={styles.card}>
          <h4>Last Week</h4>
          <p>{summary.lastWeek.toLocaleString()} </p>
        </div>
        <div className={styles.card}>
          <h4>This Month</h4>
          <p>{summary.thisMonth.toLocaleString()} </p>
        </div>
      </div>

      {/* ===== TODAY (SYSTEM VERIFIED) ===== */}
      <div className={styles.summary}>
        <div className={styles.card}>
          <h4>Today (Total)</h4>
          <p>{todayBreakdown.total.toLocaleString()} T</p>
        </div>
        <div className={styles.card}>
          <h4>Level 1 (15%)</h4>
          <p>{todayBreakdown.level1.toLocaleString()} </p>
        </div>
        <div className={styles.card}>
          <h4>Level 2 (10%)</h4>
          <p>{todayBreakdown.level2.toLocaleString()} </p>
        </div>
        <div className={styles.card}>
          <h4>Level 3 (5%)</h4>
          <p>{todayBreakdown.level3.toLocaleString()} </p>
        </div>
      </div>

      {/* ===== TABLE ===== */}
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Amount (TSH)</th>
            <th>Level</th>
            <th>Status</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {commissions.map((c) => {
            let statusClass = "";
            if (c.status === "waiting") statusClass = styles.waiting;
            if (c.status === "processing") statusClass = styles.processing;
            if (c.status === "success") statusClass = styles.success;

            return (
              <tr key={c.id}>
                <td>{Number(c.amount).toLocaleString()}</td>
                <td>Level {c.level}</td>
                <td className={statusClass}>{c.status}</td>
                <td>
                  {c.changedAt
                    ? c.changedAt.toLocaleString("en-GB")
                    : "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default Commission;